import * as THREE from 'three';
import { tweenNumber, type Tween } from './tween';
import type { MarkerManager } from '../markers';
import type { PolygonFillManager, FillState, FillColorTokens } from '../fills';
import type { InteractionDispatcher } from '../interactions';

/**
 * Layer composition facade. Public API: `engine.layers.{texture,outline,markers}`.
 * Maps the §7b.4 spec onto the underlying subsystems (texture sphere,
 * borders mesh, polygon fills, marker manager, interaction dispatcher).
 *
 * Late-bound subsystems (borders mesh, dispatcher) attach via `attachBorders`
 * / `attachDispatcher` after `loadBorders` resolves. Calls made before
 * those attach are remembered as desired state and applied on attach —
 * callers don't have to wait for "ready" to start configuring layers.
 */

/** Default tween duration for layer-level visibility / opacity changes. */
const DEFAULT_TRANSITION_MS = 400;

const clamp01 = (n: number): number => Math.max(0, Math.min(1, n));

export interface LayerManagerOptions {
  surface: THREE.Mesh;
  fills: PolygonFillManager;
  markers: MarkerManager;
  /** Layer-level transition duration in ms. Defaults to 400. */
  transitionMs?: number;
}

export class LayerManager {
  readonly texture: TextureLayer;
  readonly outline: OutlineLayer;
  /**
   * Same MarkerManager instance also reachable via `engine.markers`. Two
   * access points to the same object — the layer-grouped path keeps API
   * symmetry with `texture` and `outline`; the direct path is convenient
   * for code that mostly cares about markers.
   */
  readonly markers: MarkerManager;

  constructor(options: LayerManagerOptions) {
    const ms = options.transitionMs ?? DEFAULT_TRANSITION_MS;
    this.texture = new TextureLayer(options.surface, ms);
    this.outline = new OutlineLayer(options.fills, ms);
    this.markers = options.markers;
  }

  /** Update the shared transition duration for texture and outline tweens. */
  setTransitionMs(ms: number): void {
    this.texture.setTransitionMs(ms);
    this.outline.setTransitionMs(ms);
  }
}

/** Texture sphere layer — the photo globe surface. */
export class TextureLayer {
  private readonly surface: THREE.Mesh;
  private readonly material: THREE.MeshBasicMaterial;
  private opacityTween: Tween | null = null;
  private transitionMs: number;

  constructor(surface: THREE.Mesh, transitionMs: number) {
    this.surface = surface;
    this.material = surface.material as THREE.MeshBasicMaterial;
    this.transitionMs = transitionMs;
  }

  setVisible(visible: boolean): void {
    this.surface.visible = visible;
  }

  isVisible(): boolean {
    return this.surface.visible;
  }

  /** Tween the texture's opacity. Pass `animate: false` for an instant change. */
  setOpacity(target: number, animate = true): void {
    const clamped = clamp01(target);
    this.opacityTween?.cancel();
    if (!animate || this.transitionMs <= 0) {
      this.material.opacity = clamped;
      return;
    }
    const mat = this.material;
    this.opacityTween = tweenNumber({
      from: mat.opacity,
      to: clamped,
      ms: this.transitionMs,
      onUpdate: (v) => {
        mat.opacity = v;
      },
    });
  }

  getOpacity(): number {
    return this.material.opacity;
  }

  setTransitionMs(ms: number): void {
    this.transitionMs = ms;
  }
}

/**
 * Outline layer — borders + fills, plus the hover toggle that drives
 * country-fill highlight on pointer move.
 */
export class OutlineLayer {
  private readonly fills: PolygonFillManager;
  private bordersMesh: THREE.LineSegments | null = null;
  private bordersMaterial: THREE.LineBasicMaterial | null = null;
  private bordersOpacityTween: Tween | null = null;
  private dispatcher: InteractionDispatcher | null = null;
  private transitionMs: number;

  // Pre-attach desired state — applied to mesh/dispatcher when they arrive.
  private desiredBordersVisible = true;
  private desiredBordersOpacity = 1.0;
  private desiredHoverEnabled = true;

  constructor(fills: PolygonFillManager, transitionMs: number) {
    this.fills = fills;
    this.transitionMs = transitionMs;
  }

  attachBorders(mesh: THREE.LineSegments): void {
    this.bordersMesh = mesh;
    this.bordersMaterial = mesh.material as THREE.LineBasicMaterial;
    mesh.visible = this.desiredBordersVisible;
    this.bordersMaterial.opacity = this.desiredBordersOpacity;
  }

  attachDispatcher(dispatcher: InteractionDispatcher): void {
    this.dispatcher = dispatcher;
    dispatcher.setHoverEnabled(this.desiredHoverEnabled);
  }

  /** Composite show/hide for both borders and fills. */
  setVisible(visible: boolean): void {
    this.setBordersVisible(visible);
    this.setFillsVisible(visible);
  }

  /** Composite opacity tween for both borders and fills. */
  setOpacity(target: number, animate = true): void {
    this.setBordersOpacity(target, animate);
    this.setFillOpacity(target, animate);
  }

  setBordersVisible(visible: boolean): void {
    this.desiredBordersVisible = visible;
    if (this.bordersMesh) this.bordersMesh.visible = visible;
  }

  isBordersVisible(): boolean {
    return this.bordersMesh?.visible === true;
  }

  setBordersOpacity(target: number, animate = true): void {
    const clamped = clamp01(target);
    this.desiredBordersOpacity = clamped;
    const mat = this.bordersMaterial;
    if (!mat) return;
    this.bordersOpacityTween?.cancel();
    if (!animate || this.transitionMs <= 0) {
      mat.opacity = clamped;
      return;
    }
    this.bordersOpacityTween = tweenNumber({
      from: mat.opacity,
      to: clamped,
      ms: this.transitionMs,
      onUpdate: (v) => {
        mat.opacity = v;
      },
    });
  }

  getBordersOpacity(): number {
    return this.bordersMaterial?.opacity ?? this.desiredBordersOpacity;
  }

  setFillsVisible(visible: boolean): void {
    this.fills.setVisible(visible);
  }

  isFillsVisible(): boolean {
    return this.fills.isVisible();
  }

  setFillOpacity(target: number, animate = true): void {
    this.fills.setOpacity(target, animate);
  }

  getFillOpacity(): number {
    return this.fills.getOpacity();
  }

  setHoverEnabled(enabled: boolean): void {
    this.desiredHoverEnabled = enabled;
    this.dispatcher?.setHoverEnabled(enabled);
  }

  isHoverEnabled(): boolean {
    return this.desiredHoverEnabled;
  }

  /**
   * Set or clear an explicit fill state for one country. Pass `null` to
   * revert to base / hover.
   */
  setFillState(iso: string, state: FillState | null): void {
    this.fills.setFillState(iso, state);
  }

  /** Reset every country's explicit fill state. Hover state is preserved. */
  clearFillStates(): void {
    this.fills.clearFillStates();
  }

  /** Override one or more fill color tokens. Existing state crossfades to new colors. */
  setColorTokens(partial: Partial<FillColorTokens>): void {
    this.fills.setColorTokens(partial);
  }

  getColorTokens(): FillColorTokens {
    return this.fills.getColorTokens();
  }

  setTransitionMs(ms: number): void {
    this.transitionMs = ms;
  }
}
